import { ApSimpleDropdown } from '@alixpartners/ui-components';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import React from 'react';
import { CHART_COLOR_PALLETTE, EXPORT_OPTIONS } from 'utils/constants';
import { getFormattedCurrency, getFormattedPercentage } from 'utils/format';
import {
  IApplicationSpendItem,
  ICurrencyFormat,
  IDisplayByType,
} from 'utils/types';
import * as htmlToImage from 'html-to-image';
import { downloadAsFile } from 'utils/download';
import { ApplicationsContext } from '../ApplicationsContext';

import { PieChart } from './PieChart';
import {
  ChartDescription,
  ChartDescriptionMain,
  ChartDescriptionSecondary,
  ChartContainer,
  ChartWrapper,
  Container,
  Header,
  HeaderDropdownContainer,
  HeaderLabel,
} from './PieChart.styles';

const OTHERS = 'Others';
const SELECTED_APP_COLOR = '#b2420c';

const EMPTY_APP = {
  applicationName: '',
  applicationType: '',
  businessOwner: '',
  contribution: '',
  groupName: '',
  spend: 0,
  headcount: 0,
  fte: 0,
};

export const TopApplicationsContainerView = ({
  applications,
  displayBy,
  downloadApplicationPieData,
  format,
  selectedGroup,
  selectedApplication,
  setHoveredApplication,
  showSuccessNotification,
}: {
  applications: IApplicationSpendItem[];
  displayBy: IDisplayByType;
  downloadApplicationPieData: () => Promise<any>;
  format: ICurrencyFormat;
  selectedGroup?: string;
  selectedApplication?: string;
  setHoveredApplication: (s?: string) => void;
  showSuccessNotification: () => void;
}) => {
  const chartContainerId = `TXR_top_applications-${moment().format()}`;
  const onExportHtmlChartAsPngClick = () => {
    const chart = document.getElementById(chartContainerId);
    if (chart) {
      htmlToImage.toPng(chart).then((dataUrl) => {
        downloadAsFile(dataUrl, chartContainerId);
      });
    }
  };

  const orderProp = displayBy === IDisplayByType.COST ? 'spend' : 'headcount';
  const groupedApps = groupBy(applications, 'applicationName');
  const aggregatedApps: IApplicationSpendItem[] = Object.keys(groupedApps).map(
    (appName: string) => {
      return groupedApps[appName].reduce((acc, app) => {
        return {
          ...app,
          spend: acc.spend + app.spend,
          headcount: acc.headcount + app.headcount,
        };
      }, EMPTY_APP);
    }
  );
  const orderedApps = orderBy(aggregatedApps, orderProp, 'desc');
  let apps = orderedApps;
  let otherApps: IApplicationSpendItem[] = [];
  const maxApps = selectedGroup ? 5 : 10;
  if (orderedApps.length > maxApps) {
    apps = orderedApps.splice(0, maxApps);
    otherApps = orderedApps;
  }
  const total = aggregatedApps.reduce((acc, app) => acc + app[orderProp], 0);
  const getYValue = (value: number, total: number) => (value / total) * 100;
  const pieData = apps.map((app) => {
    const value = app[orderProp];
    const yValue = getYValue(value, total);
    return {
      name: app.applicationName,
      value: getFormattedPercentage(yValue, format),
      y: yValue,
      spend: getFormattedCurrency({
        currencyFormat: format,
        value,
        isCurrencySign: true,
        isIdealDenomination: true,
      }),
      color:
        app.applicationName === selectedApplication
          ? SELECTED_APP_COLOR
          : CHART_COLOR_PALLETTE[0],
    };
  });
  const othersValue = otherApps.reduce((acc, app) => acc + app[orderProp], 0);
  const othersYValue = getYValue(othersValue, total);
  const otherAppsData = {
    name: OTHERS,
    value: getFormattedPercentage(othersYValue, format),
    y: othersYValue,
    spend: getFormattedCurrency({
      currencyFormat: format,
      value: othersValue,
      isCurrencySign: true,
      isIdealDenomination: true,
    }),
    color: CHART_COLOR_PALLETTE[1],
  };
  const appsPercentageOfTotal = 100 - otherAppsData.y;
  const data = pieData;
  if (otherApps.length !== 0) {
    data.push(otherAppsData);
  }
  return (
    <Container>
      <Header>
        <HeaderLabel>
          {selectedGroup ? (
            <span>{selectedGroup} top applications</span>
          ) : (
            'Top applications'
          )}
        </HeaderLabel>
        <HeaderDropdownContainer>
          <ApSimpleDropdown
            iconName="outline_save_alt"
            onChange={(opt: any) => {
              if (opt.value === 'png') {
                onExportHtmlChartAsPngClick();
              } else if (opt.value === 'xlsx') {
                downloadApplicationPieData().then(showSuccessNotification);
              }
            }}
            options={EXPORT_OPTIONS}
            label="Export"
          />
        </HeaderDropdownContainer>
      </Header>
      <ChartContainer>
        <ChartWrapper id={chartContainerId}>
          <ChartDescription>
            <ChartDescriptionMain>
              {getFormattedPercentage(appsPercentageOfTotal, format)}
            </ChartDescriptionMain>
            <ChartDescriptionSecondary>
              {selectedGroup
                ? `of group ${orderProp}`
                : `of total ${orderProp}`}
            </ChartDescriptionSecondary>
          </ChartDescription>
          <PieChart
            data={data}
            onHover={(application) => {
              setHoveredApplication && setHoveredApplication(application);
            }}
          />
        </ChartWrapper>
      </ChartContainer>
    </Container>
  );
};

export const TopApplicationsContainer = (props: any) => (
  <ApplicationsContext.Consumer>
    {({ setHoveredApplication }) => (
      <TopApplicationsContainerView
        {...props}
        setHoveredApplication={setHoveredApplication}
      />
    )}
  </ApplicationsContext.Consumer>
);
